<script setup lang="ts">
import {
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  watch
} from "vue";
import { useEstadisticasStore } from "../../services/estadisticas";
import {
  collection,
  getFirestore,
  limit,
  query,
  Timestamp,
  where,
  orderBy
} from "firebase/firestore";
import Lucide, { Icon } from "../../base-components/Lucide/Lucide.vue";
import { useCollection } from "vuefire";
import { userUserStore } from "../../stores/user";
import { formatearFechaAAAAMMDD, getCurrentDate } from "../../utils/fecha";
import LineChartFecha from "../../components/LineChartFecha";
import PieChart from "../../components/PieChart";
import { pedidoConverter } from "@fixeduy/restaurantestypes";
import Table from "../../base-components/Table";
import { useMesasStore } from "../../services/mesas";
import { useSectoresStore } from "../../services/sectores";

const userStore = userUserStore();
const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();

const tabSeleccionado = ref("dia");
const estadisticasStore = useEstadisticasStore();

const inicioDelDia = new Date();
inicioDelDia.setHours(0, 0, 0, 0);

const finDelDia = new Date();
finDelDia.setHours(23, 59, 59, 999);

const fechaInicio = ref(inicioDelDia.getTime());
const fechaFin = ref(finDelDia.getTime());
const ingresos = ref(0);
const pedidosAbiertos = ref(0);
const pedidosPorSector = ref<{ [key: string]: number }>({});
const totalPedidos = ref(0);
const totalPedidosSalon = ref(0);
const totalPedidosDelivery = ref(0);
const totalPedidosTakeAway = ref(0);

const labelsHora = ref();

const promedioPorTicket = computed(() => {
  return totalPedidos.value ? ingresos.value / totalPedidos.value : 0;
});

onBeforeMount(async () => {
  await mesasStore.getFromFirestore();
  await sectoresStore.getFromFirestore();
  await calcularEstadisticas("dia");
  await cargarGraficas();
});

const db = getFirestore();
const date = new Date();

const hoy = formatearFechaAAAAMMDD(getCurrentDate());
const ayer = new Date(hoy);
ayer.setDate(date.getDate() - 1);
const ayerString = `${ayer.getFullYear()}-${String(
  ayer.getMonth() + 1
).padStart(2, "0")}-${String(ayer.getDate()).padStart(2, "0")}`;
// Para semanal

const inicioSemana = new Date(hoy);
const dia = date.getDay(); // 0 = domingo, 1 = lunes...

const difLunes = dia === 0 ? -6 : 1 - dia;
inicioSemana.setDate(date.getDate() + difLunes);
const semanal = `${inicioSemana.getFullYear()}-${(inicioSemana.getMonth() + 1).toString().padStart(2, "0")}-${inicioSemana.getDate().toString().padStart(2, "0")}`;

// Para mensual
const inicioMes = new Date(date.getFullYear(), date.getMonth(), 1);
const mensual = `${inicioMes.getFullYear()}-${(inicioMes.getMonth() + 1).toString().padStart(2, "0")}-${inicioMes.getDate().toString().padStart(2, "0")}`;

const franjasTotalCantidad: any = ref([]);

const mesasOcupadas = useCollection(
  query(
    collection(db, `empresas/${userStore.empresa?.id}/mesas`),
    where("estado", "in", ["ocupada", "ocupado"])
  )
);

const estadisticasPedidosHoy = useCollection(
  query(
    collection(
      db,
      `empresas/${userStore.empresa?.id}/estadisticas-pedidos/${hoy}/franjas`
    )
  )
);

const estadisticasPedidosAyer = useCollection(
  query(
    collection(
      db,
      `empresas/${userStore.empresa?.id}/estadisticas-pedidos/${ayerString}/franjas`
    )
  )
);

watch(
  () => estadisticasPedidosHoy.value,
  newData => {
    cargarGraficas();
  },
  { deep: true }
);

const mapearDatos = (labels: string[], data: any[]) => {
  const mapa = Object.fromEntries(data.map(d => [d.id, d.importe]));

  return labels.map(label => mapa[label] ?? 0);
};
const cargarGraficas = async () => {
  if (!estadisticasPedidosHoy && !estadisticasPedidosAyer) return;

  const ordenadas = [...estadisticasPedidosHoy.value].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  const ordenadasAyer = [...estadisticasPedidosAyer.value].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  const toMinutos = (hora: string) => {
    const [h, m] = hora.split(":").map(Number);
    return h * 60 + m;
  };

  const labels = Array.from(
    new Set([...ordenadas.map(f => f.id), ...ordenadasAyer.map(f => f.id)])
  ).sort((a, b) => toMinutos(a) - toMinutos(b));

  labelsHora.value = labels;
  const porHoraHoy = mapearDatos(labels, ordenadas);
  const porHoraAyer = mapearDatos(labels, ordenadasAyer);

  franjasTotalCantidad.value = [
    {
      label: "Hoy",
      data: porHoraHoy,
      borderColor: "#751B4E",
      tension: 0.4
    },
    {
      label: "Ayer",
      data: porHoraAyer,
      borderColor: "#E09C2D",
      tension: 0.4
    }
  ];
};

const calcularEstadisticas = async (periodo: string) => {
  tabSeleccionado.value = periodo;
  if (periodo === "dia") {
    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);

    const fin = new Date();
    fin.setHours(23, 59, 59, 999);

    fechaInicio.value = inicio.getTime();
    fechaFin.value = fin.getTime();

    // Productos
    estadisticasProductos.value = estadisticasProductosDiario.value;
  } else if (periodo === "semana") {
    const hoy = new Date();

    const inicio = new Date(hoy);
    const dia = hoy.getDay(); // 0 = domingo, 1 = lunes...

    const difLunes = dia === 0 ? -6 : 1 - dia;
    inicio.setDate(hoy.getDate() + difLunes);
    inicio.setHours(0, 0, 0, 0);

    const fin = new Date(inicio);
    fin.setDate(inicio.getDate() + 6);
    fin.setHours(23, 59, 59, 999);

    fechaInicio.value = inicio.getTime();
    fechaFin.value = fin.getTime();

    // Productos
    estadisticasProductos.value = estadisticasProductosSemanal.value;
  } else if (periodo === "mes") {
    const hoy = new Date();

    const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    inicio.setHours(0, 0, 0, 0);

    const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    fin.setHours(23, 59, 59, 999);

    fechaInicio.value = inicio.getTime();
    fechaFin.value = fin.getTime();

    // Productos
    estadisticasProductos.value = estadisticasProductosMensual.value;
  }

  ingresos.value = await estadisticasStore.getIngresos(
    fechaInicio.value,
    fechaFin.value,
    ""
  );

  pedidosAbiertos.value = await estadisticasStore.getPedidosAbiertos(
    fechaInicio.value,
    fechaFin.value
  );

  totalPedidos.value = await estadisticasStore.getTotalPedidos(
    fechaInicio.value,
    fechaFin.value
  );

  for (const sector of sectoresStore.sectores) {
    pedidosPorSector.value[sector.id] = await estadisticasStore.getIngresos(
      fechaInicio.value,
      fechaFin.value,
      sector.id
    );
  }

  // totalPedidosSalon.value = await estadisticasStore.getIngresos(
  //   fechaInicio.value,
  //   fechaFin.value,
  //   "salon"
  // );
  // totalPedidosDelivery.value = await estadisticasStore.getIngresos(
  //   fechaInicio.value,
  //   fechaFin.value,
  //   "delivery"
  // );
  // totalPedidosTakeAway.value = await estadisticasStore.getIngresos(
  //   fechaInicio.value,
  //   fechaFin.value,
  //   "takeaway"
  // );
};

const docs = useCollection(
  query(
    collection(db, `empresas/${userStore.empresa!.id}/pedidos/`),
    where("fechaCreacion", ">=", fechaInicio.value),
    where("fechaCreacion", "<=", fechaFin.value)
  ).withConverter(pedidoConverter)
);

watch(
  () => docs.value,
  newData => {
    calcularEstadisticas(tabSeleccionado.value);
  },
  { deep: true }
);

const colores = [
  "#751B4E",
  "#74AA81",
  "#E09C2D",
  "#B3617F",
  "#74AA81",
  "#1B6075",
  "#4D1F7C"
];

const chartItems = computed(() => {
  return sectoresStore.sectores.map((sector, index) => ({
    label: sector.nombre,
    value: pedidosPorSector.value[sector.id] || 0,
    icon: "Circle",
    color: colores[index % colores.length]
  }));
});

// const chartItems = computed(() => [
//   {
//     label: "Delivery",
//     value: totalPedidosDelivery.value,
//     icon: "Bike",
//     color: "#751B4E"
//   },
//   {
//     label: "Salón",
//     value: totalPedidosSalon.value,
//     icon: "Utensils",
//     color: "#74AA81"
//   },
//   {
//     label: "Take Away",
//     value: totalPedidosTakeAway.value,
//     icon: "Package",
//     color: "#E09C2D"
//   }
// ]);
const chartMediosPago = computed(() => [
  {
    label: "Efectivo",
    value: ingresos.value,
    icon: "Coins",
    color: "#751B4E"
  },
  {
    label: "Tarjeta",
    value: 0,
    icon: "CreditCard",
    color: "#74AA81"
  },

  {
    label: "Transferencia",
    value: 0,
    icon: "Handshake",
    color: "#E09C2D"
  }
]);

const pieChartData = computed(() => {
  const map: Record<string, number> = {};

  chartItems.value.forEach(item => {
    map[item.label] = Number(item.value.toFixed(2));
  });

  return map;
});

const pieChartMediosPagoData = computed(() => {
  const map: Record<string, number> = {};

  chartMediosPago.value.forEach(item => {
    map[item.label] = Number(item.value.toFixed(2));
  });

  return map;
});
const estadisticasProductos: Ref<Array<{ [key: string]: string | Number }>> =
  ref([]);

const estadisticasProductosDiario = useCollection(
  query(
    collection(
      db,
      `empresas/${userStore.empresa?.id}/estadisticas-productos-diario/${hoy}/productos`
    ),
    orderBy("cantidad", "desc"),
    limit(5)
  )
);

const estadisticasProductosSemanal = useCollection(
  query(
    collection(
      db,
      `empresas/${userStore.empresa?.id}/estadisticas-productos-semanal/${semanal}/productos`
    ),
    orderBy("cantidad", "desc"),
    limit(5)
  )
);

const estadisticasProductosMensual = useCollection(
  query(
    collection(
      db,
      `empresas/${userStore.empresa?.id}/estadisticas-productos-mensual/${mensual}/productos`
    ),
    orderBy("cantidad", "desc"),
    limit(5)
  )
);

const mostrarSeccion: Ref<{ [key: string]: boolean }> = ref({
  ingresos: true,
  vendidos: true,
  promedio: true,
  abiertos: true,
  grafica: true,
  canales: true,
  mediosPago: true,
  productos: true,
  demoras: true,
  mesas: true
});

const handleMostrarSeccion = (seccion: string) => {
  mostrarSeccion.value[seccion] = !mostrarSeccion.value[seccion];
};

const width = ref(window.innerWidth);

const handleResize = () => {
  width.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

const chartSize = computed(() => {
  return width.value > 1180 ? 140 : 80;
});
</script>
<template>
  <div class="fixed inset-0 -z-10"></div>
  <div class="w-full flex justify-end">
    <div
      class="p-1 mb-8 rounded-3xl w-60 border-slate-300 dark:border-darkmode-300 bg-[#FFFFFF40]/25 flex mr-10"
    >
      <button
        class="w-full py-1.5 px-2 rounded-3xl text-black font-normal"
        :class="{ 'bg-[#810C511A]/10': tabSeleccionado === 'dia' }"
        @click="calcularEstadisticas('dia')"
      >
        Día
      </button>

      <button
        class="w-full py-1.5 px-2 rounded-3xl text-black font-normal"
        :class="{ 'bg-[#810C511A]/10': tabSeleccionado === 'semana' }"
        @click="calcularEstadisticas('semana')"
      >
        Semana
      </button>

      <button
        class="w-full py-1.5 px-2 rounded-3xl text-black font-normal"
        :class="{ 'bg-[#810C511A]/10': tabSeleccionado === 'mes' }"
        @click="calcularEstadisticas('mes')"
      >
        Mes
      </button>
    </div>
  </div>
  <div class="flex gap-4 ml-4 mr-10">
    <div
      :class="[
        'relative font-bold flex-1 bg-white/25 py-8 px-16 rounded-xl border border-[#810C5133]/20 shadow-lg',
        !mostrarSeccion['ingresos']
          ? 'flex items-center justify-center text-center text-2xl'
          : 'text-center text-xl'
      ]"
    >
      <div :class="['text-slate-600 ', mostrarSeccion['ingresos'] ? '' : '']">
        Ingresos
      </div>

      <div
        v-if="mostrarSeccion['ingresos']"
        class="text-slate-700 mt-5 text-2xl"
      >
        ${{ ingresos.toFixed(2) }}
      </div>

      <Lucide
        :icon="mostrarSeccion['ingresos'] ? 'EyeOff' : 'Eye'"
        @click="handleMostrarSeccion('ingresos')"
        class="absolute right-6 bottom-4 cursor-pointer"
      />
    </div>
    <div
      :class="[
        'relative font-bold flex-1 bg-white/25 py-8 px-16 rounded-xl border border-[#810C5133]/20 shadow-lg',
        !mostrarSeccion['vendidos']
          ? 'flex items-center justify-center text-center text-2xl'
          : 'text-center text-xl'
      ]"
    >
      <div :class="['text-slate-600 ', mostrarSeccion['vendidos'] ? '' : '']">
        Pedidos vendidos
      </div>

      <div
        v-if="mostrarSeccion['vendidos']"
        class="mt-5 text-2xl flex items-center justify-center gap-2"
      >
        <Lucide icon="ReceiptText"></Lucide>
        <span>{{ totalPedidos }}</span>
      </div>

      <Lucide
        :icon="mostrarSeccion['vendidos'] ? 'EyeOff' : 'Eye'"
        @click="handleMostrarSeccion('vendidos')"
        class="absolute right-6 bottom-4 cursor-pointer"
      />
    </div>

    <div
      :class="[
        'relative font-bold flex-1 bg-white/25 py-8 px-16 rounded-xl border border-[#810C5133]/20 shadow-lg',
        !mostrarSeccion['promedio']
          ? 'flex items-center justify-center text-center text-2xl'
          : 'text-center text-xl'
      ]"
    >
      <div :class="['text-slate-600 ', mostrarSeccion['promedio'] ? '' : '']">
        Promedio por pedido
      </div>

      <div
        v-if="mostrarSeccion['promedio']"
        class="text-slate-700 mt-5 text-2xl"
      >
        ${{ promedioPorTicket.toFixed(2) }}
      </div>

      <Lucide
        :icon="mostrarSeccion['promedio'] ? 'EyeOff' : 'Eye'"
        @click="handleMostrarSeccion('promedio')"
        class="absolute right-6 bottom-4 cursor-pointer"
      />
    </div>

    <div
      :class="[
        'relative font-bold flex-1 bg-white/25 py-8 px-16 rounded-xl border border-[#810C5133]/20 shadow-lg',
        !mostrarSeccion['abiertos']
          ? 'flex items-center justify-center text-center text-2xl'
          : 'text-center text-xl'
      ]"
    >
      <div :class="['text-slate-600 ', mostrarSeccion['abiertos'] ? '' : '']">
        Pedidos abiertos
      </div>

      <div
        v-if="mostrarSeccion['abiertos']"
        class="mt-5 text-2xl flex items-center justify-center gap-2"
      >
        <Lucide icon="NotepadText"></Lucide>
        <span>{{ pedidosAbiertos }}</span>
      </div>

      <Lucide
        :icon="mostrarSeccion['abiertos'] ? 'EyeOff' : 'Eye'"
        @click="handleMostrarSeccion('abiertos')"
        class="absolute right-6 bottom-4 cursor-pointer"
      />
    </div>
  </div>

  <div class="grid grid-cols-4 gap-6 mt-6 mr-10 ml-4 items-start">
    <div
      class="relative col-span-3 bg-white/25 py-8 px-16 rounded-xl border border-[#810C5133]/20 shadow-lg"
    >
      <template v-if="mostrarSeccion['grafica']">
        <h2 class="text-base font-semibold">Ventas por hora</h2>

        <LineChartFecha
          :datasets="franjasTotalCantidad"
          :labels="labelsHora"
          :height="400"
        />
      </template>

      <div
        v-else
        class="h-[450px] flex items-center justify-center text-2xl font-semibold text-slate-600"
      >
        <LineChartFecha
          :datasets="[]"
          :labels="labelsHora"
          :height="400"
          :width="1000"
        />
      </div>

      <Lucide
        :icon="mostrarSeccion['grafica'] ? 'EyeOff' : 'Eye'"
        @click="handleMostrarSeccion('grafica')"
        class="absolute right-6 bottom-4 cursor-pointer"
      />
    </div>
    <div class="flex flex-col gap-6">
      <div
        class="relative bg-white/25 p-6 rounded-xl border border-[#810C5133]/20 shadow-lg flex flex-col h-fit"
      >
        <template v-if="mostrarSeccion['canales']">
          <h3 class="text-base font-semibold mb-6">Ventas por sector</h3>

          <div class="flex items-center gap-0 xl:gap-8 min-w-0">
            <div class="flex flex-col gap-3">
              <div
                v-for="item in chartItems"
                :key="item.label"
                class="flex items-center gap-3"
              >
                <Lucide
                  :icon="item.icon as Icon"
                  :style="{ color: item.color }"
                  class="w-4 h-4"
                />

                <div class="flex flex-col leading-tight">
                  <span class="font-semibold">
                    ${{ Number(item.value).toFixed(2) }}
                  </span>

                  <span class="text-xs text-slate-500">
                    {{ item.label }}
                  </span>
                </div>
              </div>
            </div>

            <div class="w-[140px] h-[140px] shrink-0">
              <PieChart
                :dataMap="pieChartData"
                :width="chartSize"
                :height="chartSize"
              />
            </div>
          </div>
        </template>

        <div
          v-else
          class="flex-1 flex items-center justify-center text-xl font-semibold text-slate-600 min-h-[180px]"
        >
          Ventas por canal
        </div>

        <Lucide
          :icon="mostrarSeccion['canales'] ? 'EyeOff' : 'Eye'"
          @click="handleMostrarSeccion('canales')"
          class="absolute right-4 bottom-4 cursor-pointer"
        />
      </div>

      <div
        class="relative bg-white/25 p-6 rounded-xl border border-[#810C5133]/20 shadow-lg flex flex-col h-fit"
      >
        <template v-if="mostrarSeccion['mediosPago']">
          <h3 class="text-base font-semibold mb-6">Ventas por medio de pago</h3>

          <div class="flex items-center gap-0 xl:gap-8 min-w-0">
            <div class="flex flex-col gap-3">
              <div
                v-for="item in chartMediosPago"
                :key="item.label"
                class="flex items-center gap-3"
              >
                <Lucide
                  :icon="item.icon as Icon"
                  :style="{ color: item.color }"
                  class="w-4 h-4"
                />

                <div class="flex flex-col leading-tight">
                  <span class="font-semibold">
                    ${{ item.value.toFixed(2) }}
                  </span>

                  <span class="text-xs text-slate-500">
                    {{ item.label }}
                  </span>
                </div>
              </div>
            </div>

            <div class="w-[140px] h-[140px] shrink-0">
              <PieChart
                :dataMap="pieChartMediosPagoData"
                :width="chartSize"
                :height="chartSize"
              />
            </div>
          </div>
        </template>

        <div
          v-else
          class="flex-1 flex items-center justify-center text-xl font-semibold text-slate-600 min-h-[180px]"
        >
          Ventas por medio de pago
        </div>

        <Lucide
          :icon="mostrarSeccion['mediosPago'] ? 'EyeOff' : 'Eye'"
          @click="handleMostrarSeccion('mediosPago')"
          class="absolute right-4 bottom-4 cursor-pointer"
        />
      </div>
    </div>
  </div>

  <!--  -->

  <div class="grid grid-cols-4 gap-6 mt-6 mr-10 ml-4">
    <div
      class="relative col-span-3 bg-white/25 py-8 px-16 rounded-xl border border-[#810C5133]/20 shadow-lg"
    >
      <!-- CONTENIDO -->
      <template v-if="mostrarSeccion['productos']">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th class="whitespace-nowrap text-base font-semibold">
                Productos más vendidos
              </Table.Th>
              <Table.Th class="whitespace-nowrap"></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            <template
              v-for="(producto, index) in estadisticasProductos"
              :key="producto.productoId as string"
            >
              <Table.Tr class="hover:!bg-[#810C511A]/20">
                <Table.Td> {{ index + 1 }}. {{ producto.nombre }} </Table.Td>
                <Table.Td> {{ producto.cantidad }} ventas </Table.Td>
              </Table.Tr>
            </template>

            <Table.Tr v-if="estadisticasProductos.length === 0">
              <Table.Td colspan="30" class="text-center py-8 text-slate-500">
                No hay productos para mostrar.
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </template>

      <div
        v-else
        class="flex items-center justify-center text-xl font-semibold text-slate-600 min-h-[250px]"
      >
        Productos más vendidos
      </div>

      <Lucide
        :icon="mostrarSeccion['productos'] ? 'EyeOff' : 'Eye'"
        @click="handleMostrarSeccion('productos')"
        class="absolute right-6 bottom-4 cursor-pointer"
      />
    </div>

    <div class="grid grid-rows-2 gap-6">
      <!-- <div
        class="relative bg-white/25 p-6 rounded-xl border border-[#810C5133]/20 shadow-lg flex flex-col text-center"
      >
        <template v-if="mostrarSeccion['demoras']">
          <h3 class="text-base font-semibold mb-6">Demoras en cocina</h3>

          <div class="flex items-center justify-center gap-2 text-xl font-bold">
            <Lucide icon="Clock"></Lucide>
            15 min
          </div>
        </template>

        <div
          v-else
          class="flex-1 flex items-center justify-center text-xl font-semibold text-slate-600 min-h-[100px]"
        >
          Demoras en cocina
        </div>

        <Lucide
          :icon="mostrarSeccion['demoras'] ? 'EyeOff' : 'Eye'"
          @click="handleMostrarSeccion('demoras')"
          class="absolute right-4 bottom-4 cursor-pointer"
        />
      </div> -->

      <div
        class="relative bg-white/25 p-6 rounded-xl border border-[#810C5133]/20 shadow-lg flex flex-col text-center"
      >
        <template v-if="mostrarSeccion['mesas']">
          <h3 class="text-base font-semibold mb-6">Mesas ocupadas</h3>

          <div class="flex items-center justify-center gap-2 text-xl font-bold">
            <Lucide icon="Utensils"></Lucide>
            {{ mesasOcupadas.length }}
          </div>
        </template>

        <div
          v-else
          class="flex-1 flex items-center justify-center text-xl font-semibold text-slate-600 min-h-[100px]"
        >
          Mesas ocupadas
        </div>

        <Lucide
          :icon="mostrarSeccion['mesas'] ? 'EyeOff' : 'Eye'"
          @click="handleMostrarSeccion('mesas')"
          class="absolute right-4 bottom-4 cursor-pointer"
        />
      </div>
    </div>
  </div>
</template>
